import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  Clock,
  Check,
  FileText,
  ArrowRight,
} from 'lucide-react';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { useWalletSummary } from '@/features/wallet/hooks/useWallet';
import { useStripeConnect } from '@/hooks/useStripeConnect';
import { usePayoutCurrency } from '@/features/wallet/hooks/usePayoutCurrency';
import { formatCurrency } from '@/lib/pricing';
import { getMinimumPayoutCents } from '@/lib/payoutLimits';
import { useI18n } from '@/contexts/I18nContext';
import { SOSSIETY_RED } from '@/styles/brand';
import type { WalletActivityItem } from '@/types/wallet';

// ── Design tokens ─────────────────────────────────────────────────────────────

const CARD_BG = '#231a1a';

// ── Status config ─────────────────────────────────────────────────────────────

type StatusConfig = {
  label: string;
  statusColor: string; // plain status text color (below amount)
  amountColor: string; // amount text color
  iconBg: string;
  iconColor: string;
};

function getStatusConfig(
  status: WalletActivityItem['status'],
  type: WalletActivityItem['type'],
): StatusConfig {
  if (type === 'payout') {
    return {
      label:       'Sent',
      statusColor: '#22c55e',
      amountColor: 'rgba(255,255,255,0.7)',
      iconBg:      'rgba(255,255,255,0.06)',
      iconColor:   'rgba(255,255,255,0.5)',
    };
  }
  switch (status) {
    case 'paid':
      return {
        label:       'Awaiting acceptance',
        statusColor: '#3b82f6',
        amountColor: 'rgba(255,255,255,0.7)',
        iconBg:      'rgba(59,130,246,0.12)',
        iconColor:   '#3b82f6',
      };
    case 'in_review':
      return {
        label:       'In progress',
        statusColor: '#eab308',
        amountColor: '#fff',
        iconBg:      'rgba(234,179,8,0.12)',
        iconColor:   '#eab308',
      };
    case 'delivered':
      return {
        label:       'Delivered',
        statusColor: '#22c55e',
        amountColor: '#fff',
        iconBg:      'rgba(34,197,94,0.12)',
        iconColor:   '#22c55e',
      };
    default:
      return {
        label:       'Refunded',
        statusColor: 'rgba(255,255,255,0.4)',
        amountColor: 'rgba(255,255,255,0.4)',
        iconBg:      'rgba(255,255,255,0.06)',
        iconColor:   'rgba(255,255,255,0.4)',
      };
  }
}

// ── Activity icon ─────────────────────────────────────────────────────────────

function ActivityIcon({ item, cfg }: { item: WalletActivityItem; cfg: StatusConfig }) {
  const Icon = (() => {
    if (item.type === 'payout') return ArrowRight;
    switch (item.status) {
      case 'paid':      return FileText;
      case 'in_review': return Clock;
      case 'delivered': return Check;
      default:          return ArrowRight;
    }
  })();

  return (
    <div
      className="w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: cfg.iconBg }}
    >
      <Icon size={15} style={{ color: cfg.iconColor }} />
    </div>
  );
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function WalletSkeleton() {
  return (
    <div className="animate-pulse">
      {/* Balance card */}
      <div
        className="mx-5 mt-3 rounded-[20px] border border-white/[0.06] px-5 pt-6 pb-5"
        style={{ backgroundColor: CARD_BG }}
      >
        <div className="h-3 w-28 bg-white/10 rounded mb-3" />
        <div className="h-10 w-36 bg-white/10 rounded mb-5" />
        <div className="h-[60px] rounded-[14px] bg-white/[0.06]" />
      </div>

      {/* Stats */}
      <div className="flex gap-[10px] mx-5 mt-[14px]">
        <div
          className="flex-1 h-[90px] rounded-[16px] border border-white/[0.06]"
          style={{ backgroundColor: CARD_BG }}
        />
        <div
          className="flex-1 h-[90px] rounded-[16px] border border-white/[0.06]"
          style={{ backgroundColor: CARD_BG }}
        />
      </div>

      {/* Activity items */}
      <div className="mt-8 flex flex-col">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex items-center gap-3 px-5 py-[14px] border-b border-white/[0.06]"
          >
            <div className="w-[34px] h-[34px] rounded-full bg-white/[0.06] flex-shrink-0" />
            <div className="flex-1 flex flex-col gap-2">
              <div className="h-3 w-32 bg-white/10 rounded" />
              <div className="h-2.5 w-24 bg-white/10 rounded" />
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="h-3.5 w-14 bg-white/10 rounded" />
              <div className="h-2.5 w-20 bg-white/10 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Activity row ──────────────────────────────────────────────────────────────

function ActivityListItem({
  item,
  onPress,
}: {
  item: WalletActivityItem;
  onPress: () => void;
}) {
  const cfg          = getStatusConfig(item.status, item.type);
  const isPositive   = item.amount_cents >= 0;
  const sign         = isPositive ? '+' : '−';
  const formattedAmt = `${sign}${formatCurrency(Math.abs(item.amount_cents), item.currency)}`;

  return (
    <button
      data-testid="activity-item"
      onClick={onPress}
      className="w-full flex items-center gap-[12px] px-5 pt-[14px] pb-[15px] border-b border-white/[0.06] text-left active:bg-white/[0.02] transition-colors"
    >
      <ActivityIcon item={item} cfg={cfg} />

      {/* Label + subtitle */}
      <div className="flex-1 min-w-0 flex flex-col gap-[2px]">
        <p className="text-white text-[13px] font-medium leading-none truncate">
          {item.title}
        </p>
        <p className="text-[#8d8d8d] text-[11px] leading-none">{item.subtitle}</p>
      </div>

      {/* Amount + status (no pill — plain colored text) */}
      <div className="flex flex-col items-end gap-[2px] flex-shrink-0">
        <span
          className="text-[14px] font-semibold leading-none"
          style={{ color: cfg.amountColor }}
        >
          {formattedAmt}
        </span>
        <span
          className="text-[10px] leading-none"
          style={{ color: cfg.statusColor }}
        >
          {cfg.label}
        </span>
      </div>
    </button>
  );
}

// ── WalletPage ────────────────────────────────────────────────────────────────

const WalletPage: React.FC = () => {
  const navigate   = useNavigate();
  const { data: wallet, isLoading } = useWalletSummary();
  const { account, isLoading: stripeLoading, getLoginLink, isGettingLoginLink } =
    useStripeConnect();
  const isStripeSetup =
    !stripeLoading && account?.charges_enabled && account?.payouts_enabled;

  const { t } = useI18n();
  // The threshold the release engine actually applies, in the instructor's own
  // currency — a literal "$50" told every BRL instructor the wrong number.
  const { currency } = usePayoutCurrency();
  const formattedMinimum = formatCurrency(getMinimumPayoutCents(currency), currency);

  const handleOpenStripeExpress = async () => {
    // Open blank window synchronously inside the user gesture to avoid popup blocks
    const newWindow = window.open('', '_blank', 'noopener,noreferrer');
    try {
      const data = await getLoginLink();
      if (newWindow) newWindow.location.href = data.login_url;
    } catch (err) {
      console.error('[Stripe Express] failed to get login link:', err);
      newWindow?.close();
    }
  };

  return (
    <>
      <TopNavigation variant="subtle" />

      {/* ── Fixed title bar (sits below TopNavigation h-16) ─────────── */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-[#160c0c] border-b border-white/[0.06]">
        <div className="flex items-center justify-between px-5 py-3.5">
          <h1 className="text-[17px] font-bold text-white">Wallet</h1>
        </div>
      </div>

      <div
        className="min-h-screen bg-[#160c0c] text-white"
        style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}
      >
        {/*
          TopNavigation is fixed at top-0, h-16 (64 px).
          Title bar is fixed at top-16, h-[52px].
          Content starts at top-[116px] → pt-[116px].
        */}
        <div className="pt-[116px] pb-28">

          {/* ── Wallet setup banner ───────────────────────────────────── */}
          {!stripeLoading && !isStripeSetup && (
            <div className="px-5 mb-3">
              <button
                onClick={() => navigate('/settings/instructor-tools/earnings')}
                className="w-full text-left rounded-[16px] p-4 flex items-center gap-3 transition-opacity active:opacity-80"
                style={{
                  backgroundColor: 'rgba(215,46,22,0.08)',
                  border:          '1px solid rgba(215,46,22,0.25)',
                }}
              >
                <div
                  className="w-[34px] h-[34px] rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'rgba(215,46,22,0.15)' }}
                >
                  <ArrowRight size={15} style={{ color: SOSSIETY_RED }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-[13px] font-medium">Set up your wallet</p>
                  <p className="text-[11px] mt-0.5" style={{ color: '#8d8d8d' }}>
                    Complete Stripe verification to start receiving payouts.
                  </p>
                </div>
              </button>
            </div>
          )}

          {isLoading ? (
            <WalletSkeleton />
          ) : (
            <>
              {/* ── Balance card ──────────────────────────────────────── */}
              <div
                className="mx-5 rounded-[20px] border border-white/[0.06] px-5 pt-6 pb-5"
                style={{ backgroundColor: CARD_BG }}
              >
                {/* Label */}
                <p className="text-[#8d8d8d] text-[12px] leading-none mb-[10px]">
                  Available balance
                </p>

                {/* Amount — integer at 42px, decimals at 22px dimmer */}
                {(() => {
                  const raw    = formatCurrency(wallet?.available_balance_cents ?? 0, 'USD');
                  const dotIdx = raw.lastIndexOf('.');
                  const main   = dotIdx >= 0 ? raw.slice(0, dotIdx) : raw;
                  const cents  = dotIdx >= 0 ? raw.slice(dotIdx)    : '';
                  return (
                    <p
                      data-testid="available-balance"
                      className="font-bold flex items-baseline leading-none mb-[17px]"
                    >
                      <span className="text-white text-[42px]">{main}</span>
                      {cents && (
                        <span
                          className="text-[22px]"
                          style={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                          {cents}
                        </span>
                      )}
                    </p>
                  );
                })()}

                {/* Next payout row */}
                <div
                  className="flex items-center gap-[10px] rounded-[14px] p-[14px]"
                  style={{ backgroundColor: 'rgba(215,46,22,0.08)' }}
                >
                  {/* Red circle icon */}
                  <div
                    className="w-[32px] h-[32px] rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: SOSSIETY_RED }}
                  >
                    <CalendarDays size={16} color="white" />
                  </div>

                  {/* Text block */}
                  <div className="flex-1 min-w-0 flex flex-col gap-[2px]">
                    <p className="text-[#8d8d8d] text-[11px] leading-none">Next payout</p>
                    {wallet?.next_payout ? (
                      <p className="text-white text-[13px] font-semibold leading-none truncate">
                        {wallet.next_payout.date}
                        {wallet.next_payout.bank_last4
                          ? ` · ${wallet.next_payout.bank_type ?? 'ACH'} ••••${wallet.next_payout.bank_last4}`
                          : ' · Bank on file'}
                      </p>
                    ) : (
                      <p className="text-[#8d8d8d] text-[13px] leading-none">
                        {t('wallet.page.no_payout_scheduled', { minimum: formattedMinimum })}
                      </p>
                    )}
                  </div>

                  {/* "Auto" badge — plain bold text, no pill */}
                  {wallet?.next_payout?.automatic && (
                    <span className="text-white text-[14px] font-bold flex-shrink-0">
                      Auto
                    </span>
                  )}
                </div>
              </div>

              {/* ── Stats row ─────────────────────────────────────────── */}
              <div className="flex gap-[10px] mx-5 mt-[14px]">
                {/* This month */}
                <div
                  className="flex-1 rounded-[16px] border border-white/[0.06] px-[14px] pt-[14px] pb-[14px] flex flex-col gap-[6px]"
                  style={{ backgroundColor: CARD_BG }}
                >
                  <p className="text-[#8d8d8d] text-[11px] tracking-[0.8px] uppercase leading-none">
                    This month
                  </p>
                  <p
                    data-testid="this-month-amount"
                    className="text-white text-[22px] font-bold leading-none"
                  >
                    {formatCurrency(wallet?.this_month_cents ?? 0, 'USD')}
                  </p>
                  <p className="text-[#8d8d8d] text-[11px] leading-none">
                    {wallet?.this_month_review_count ?? 0} reviews
                  </p>
                </div>

                {/* This week */}
                <div
                  className="flex-1 rounded-[16px] border border-white/[0.06] px-[14px] pt-[14px] pb-[14px] flex flex-col gap-[6px]"
                  style={{ backgroundColor: CARD_BG }}
                >
                  <p className="text-[#8d8d8d] text-[11px] tracking-[0.8px] uppercase leading-none">
                    This week
                  </p>
                  <p
                    data-testid="this-week-amount"
                    className="text-white text-[22px] font-bold leading-none"
                  >
                    {formatCurrency(wallet?.this_week_cents ?? 0, 'USD')}
                  </p>
                  <p className="text-[#8d8d8d] text-[11px] leading-none">
                    {wallet?.this_week_review_count ?? 0} reviews
                  </p>
                </div>
              </div>

              {/* ── Activity section header ───────────────────────────── */}
              <div className="flex items-center justify-between px-5 pt-[16px] pb-[6px]">
                <p className="text-[#8d8d8d] text-[13px] font-semibold tracking-[0.8px] uppercase">
                  Activity
                </p>
                {isStripeSetup && (
                  <button
                    onClick={handleOpenStripeExpress}
                    disabled={isGettingLoginLink}
                    className="text-[12px] font-medium disabled:opacity-50 transition-opacity"
                    style={{ color: SOSSIETY_RED }}
                  >
                    {isGettingLoginLink ? 'Opening…' : 'Stripe Express'}
                  </button>
                )}
              </div>

              {/* ── Activity list ─────────────────────────────────────── */}
              {(wallet?.activity?.length ?? 0) === 0 ? (
                <div className="py-12 flex flex-col items-center gap-2">
                  <p className="text-[#8d8d8d] text-sm">No activity yet</p>
                </div>
              ) : (
                <div data-testid="activity-list" className="flex flex-col">
                  {wallet!.activity.map((item) => (
                    <ActivityListItem
                      key={item.id}
                      item={item}
                      onPress={() => {
                        if (item.roll_review_id) {
                          navigate(`/wallet/transaction/${item.roll_review_id}`);
                        }
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Footer note */}
              <p className="px-5 pt-5 text-[#8d8d8d] text-[11px]">
                {t('wallet.page.payout_note', { minimum: formattedMinimum })}
              </p>
            </>
          )}
        </div>
      </div>

      <BottomNavigation />
    </>
  );
};

export default WalletPage;
